import { ComponentDesign } from '@paragon/contract-library';
import { ChildrenFinder, getComponentDesignDescendantGuids } from '@paragon/layout-models';

export function getComponentDesignDescendantModels(
  childrenFinder: ChildrenFinder,
  componentDesigns: { readonly [componentDesignGuid: string]: ComponentDesign },
  modelGuid: string,
) {
  const componentDesignDescendantGuids = getComponentDesignDescendantGuids(modelGuid, childrenFinder);
  return Object.values(componentDesigns).filter(({ guid }) => componentDesignDescendantGuids.includes(guid));
}

export const getComponentDesignDescendantsLockingStatus = (componentDesignDescendants: readonly ComponentDesign[]) => {
  const allDescendantsHaveAnalogLocked = componentDesignDescendants.every(
    (designOrSummary) => designOrSummary.analogLocked,
  );
  const allDescendantsHaveHangersLocked = componentDesignDescendants.every(
    (designOrSummary) => designOrSummary.hangersLocked,
  );
  const allDescendantsHaveLoadingLocked = componentDesignDescendants.every(
    (designOrSummary) => designOrSummary.loadingLocked,
  );
  const allDescendantsHaveMembersLocked = componentDesignDescendants.every(
    (designOrSummary) => designOrSummary.membersLocked,
  );
  const allDescendantsHavePlatingLocked = componentDesignDescendants.every(
    (designOrSummary) => designOrSummary.platingLocked,
  );
  const someDescendantsHaveAnalogLocked = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.analogLocked,
  );
  const someDescendantsHaveHangersLocked = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.hangersLocked,
  );
  const someDescendantsHaveLoadingLocked = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.loadingLocked,
  );
  const someDescendantsHaveMembersLocked = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.membersLocked,
  );
  const someDescendantsHavePlatingLocked = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.platingLocked,
  );

  const someDescendantsHaveIndividuallyLockedHangers = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.lockedHangers.length > 0,
  );
  const someDescendantsHaveIndividuallyLockedMembers = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.lockedMembers.length > 0,
  );
  const someDescendantsHaveIndividuallyLockedPlatePairs = componentDesignDescendants.some(
    (designOrSummary) => designOrSummary.lockedPlatePairs.length > 0,
  );

  // Unlike the flags above, this also accounts for individually locked items, so it can be used to decide whether
  //   there is anything at all left for "Unlock Everything" to do.
  const someDescendantsHaveSomethingLocked =
    someDescendantsHaveAnalogLocked ||
    someDescendantsHaveHangersLocked ||
    someDescendantsHaveLoadingLocked ||
    someDescendantsHaveMembersLocked ||
    someDescendantsHavePlatingLocked ||
    someDescendantsHaveIndividuallyLockedHangers ||
    someDescendantsHaveIndividuallyLockedMembers ||
    someDescendantsHaveIndividuallyLockedPlatePairs;

  // A lock option is indeterminate when part of the scope is locked but not all of it -- the same partial state that
  //   an individual truss renders as an indeterminate checkbox, generalized across every descendant. Unlocking from
  //   there discards a deliberate mix of settings rather than one uniform choice, so it is worth confirming first.
  const someLockOptionIsIndeterminate =
    (someDescendantsHaveAnalogLocked && !allDescendantsHaveAnalogLocked) ||
    (someDescendantsHaveLoadingLocked && !allDescendantsHaveLoadingLocked) ||
    ((someDescendantsHaveHangersLocked || someDescendantsHaveIndividuallyLockedHangers) &&
      !allDescendantsHaveHangersLocked) ||
    ((someDescendantsHaveMembersLocked || someDescendantsHaveIndividuallyLockedMembers) &&
      !allDescendantsHaveMembersLocked) ||
    ((someDescendantsHavePlatingLocked || someDescendantsHaveIndividuallyLockedPlatePairs) &&
      !allDescendantsHavePlatingLocked);

  return {
    allDescendantsHaveAnalogLocked,
    allDescendantsHaveHangersLocked,
    allDescendantsHaveLoadingLocked,
    allDescendantsHaveMembersLocked,
    allDescendantsHavePlatingLocked,
    someDescendantsHaveAnalogLocked,
    someDescendantsHaveHangersLocked,
    someDescendantsHaveLoadingLocked,
    someDescendantsHaveMembersLocked,
    someDescendantsHavePlatingLocked,
    someDescendantsHaveSomethingLocked,
    someLockOptionIsIndeterminate,
    anythingIsLocked:
      allDescendantsHaveAnalogLocked ||
      allDescendantsHaveHangersLocked ||
      allDescendantsHaveLoadingLocked ||
      allDescendantsHaveMembersLocked ||
      allDescendantsHavePlatingLocked,
  };
};
